import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getWhatsAppUrl } from '../../../components/feature/WhatsAppButton';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Track scroll position to toggle navbar background/shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    // Clean‑up on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <section className="relative min-h-screen bg-white overflow-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/2063d56d77349d69e524f0264c628611/11429a8bbabca3c2bd48cc699fa75d27.png"
                alt="Tadashi Consulting"
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-gray-800 hover:text-[#D4AF37]'
              }`}
            >
              About
            </Link>
            <span className="w-px h-4 bg-[#D4AF37]" />
            <Link
              to="/residential-renovations"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-gray-800 hover:text-[#D4AF37]'
              }`}
            >
              Residential
            </Link>
            <span className="w-px h-4 bg-[#D4AF37]" />
            <Link
              to="/commercial-construction"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-gray-800 hover:text-[#D4AF37]'
              }`}
            >
              Commercial
            </Link>
            <span className="w-px h-4 bg-[#D4AF37]" />
            <a
              href="/#gallery"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-gray-800 hover:text-[#D4AF37]'
              }`}
            >
              Gallery
            </a>
            <span className="w-px h-4 bg-[#D4AF37]" />
            <a
              href="/#process"
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-gray-700 hover:text-[#D4AF37]'
                  : 'text-gray-800 hover:text-[#D4AF37]'
              }`}
            >
              Process
            </a>
            <span className="w-px h-4 bg-[#D4AF37]" />
            <a
              href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a site inspection.')}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-[#D4AF37] text-white text-sm font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-md whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Book Inspection
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i
              className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-800`}
            />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
            <div className="px-6 py-6 space-y-4">
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                About
              </Link>
              <Link
                to="/residential-renovations"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Residential
              </Link>
              <Link
                to="/commercial-construction"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Commercial
              </Link>
              <a
                href="/#gallery"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Gallery
              </a>
              <a
                href="/#process"
                onClick={() => setMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-[#D4AF37] transition-colors py-2"
              >
                Process
              </a>
              <a
                href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a site inspection.')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="block text-center px-8 py-3 bg-[#D4AF37] text-white text-sm font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-md whitespace-nowrap mt-4 flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Book Inspection
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero content */}
      <div className="relative min-h-screen flex items-center">
        {/* Background image + overlay */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a364e5cf4d3ab157f74d257cf72021c1.jpg"
            alt="Luxury Interior Renovation Johannesburg"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40 max-md:bg-gradient-to-b max-md:from-white/95 max-md:via-white/90 max-md:to-white/70" />
        </div>

        {/* Textual content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 w-full">
          <div className="max-w-3xl">
            <div className="space-y-4 md:space-y-6">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-[#2A2A2A]">
                <a href="/">
                  Luxury Interior Design, & Renovations
                  <br />
                  Built to{' '}
                  <span className="font-serif text-[#D4AF37]">Perfection</span>
                </a>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                Full-service <strong>renovation and construction</strong> for
                discerning homeowners and commercial property owners in{' '}
                <strong>Johannesburg</strong>. Licensed contractors. Turnkey
                solutions.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 pt-4 md:pt-8">
                <a
                  href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to book a site inspection for my property.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 md:px-10 py-3.5 md:py-4 bg-[#D4AF37] text-white text-sm md:text-base font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-lg flex items-center gap-3 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                  Book Site Inspection
                  <i className="ri-arrow-right-line text-lg md:text-xl" />
                </a>
                <a
                  href="/#gallery"
                  className="px-8 md:px-10 py-3.5 md:py-4 border-2 border-[#D4AF37] text-[#D4AF37] text-sm md:text-base font-semibold rounded-full hover:bg-[#D4AF37] hover:text-white transition-all whitespace-nowrap"
                >
                  View Projects
                </a>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 sm:gap-8 md:gap-12 mt-12 md:mt-20">
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl md:text-3xl text-[#D4AF37]" />
                </div>
                <span className="text-xs md:text-sm font-medium text-gray-700 text-center">
                  Licensed Builders
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                  <i className="ri-time-line text-2xl md:text-3xl text-[#D4AF37]" />
                </div>
                <span className="text-xs md:text-sm font-medium text-gray-700 text-center">
                  10+ Years
                </span>
              </div>
              <div className="flex flex-col items-center gap-1 md:gap-2">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                  <i className="ri-building-line text-2xl md:text-3xl text-[#D4AF37]" />
                </div>
                <span className="text-xs md:text-sm font-medium text-gray-700 text-center">
                  200+ Projects
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
